/**
 * lh-swym-hover.js
 * Reliable hover-reveal + white-circle styling for Swym wishlist buttons.
 *
 * Problem: Swym's own JavaScript (loaded from its CDN) sets inline styles on
 * the button element after our stylesheet loads, overriding CSS-only rules.
 * `opacity: 0 !important` in a stylesheet is defeated by an inline style of
 * `opacity: 1 !important` (inline always wins over stylesheet).
 *
 * Solution: We use a MutationObserver to detect when Swym injects/modifies its
 * button, then apply our own inline !important styles so we win the cascade.
 * Hover show/hide is then handled entirely in JS, since we own the inline styles.
 *
 * Loaded globally by sections/custom-header.liquid.
 * No library dependencies.
 */
(function () {
  'use strict';

  /* ── Tuning ── */
  var BG_DEFAULT  = '#fff';
  var BG_HOVER    = '#fff';
  var BTN_SIZE    = '44px'; /* was 36px — raised to WCAG 2.5.5 min 44×44px touch target */
  var TOP_OFFSET  = '8px';
  var RIGHT_OFFSET = '8px';

  /* Touch / no-hover devices — buttons stay permanently visible */
  var IS_TOUCH = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  /* ── Selectors ── */
  /* Product card wrappers — Stiletto + our custom sections */
  var CARD_SELECTORS = [
    '.product-item',
    '.lh-fp__card',
    '.lh-rts__card',
    '.lh-sr__card',
    '.lh-gg__card',
    '.lh-gg-grid__card',
    '.lh-bs__card',
    '.lh-bfr__card',
    '.lh-bfb__card',
    '.lh-zg__card',
    '.lh-zs__card',
    '.lhf__card',
    '.lh-gg-sc__pick',
    '.featured-collection-grid__product',
    '.product-card',
    '.grid-item'
  ];
  var CARD_SEL = CARD_SELECTORS.join(', ');

  /* Swym's injected button(s) + our own custom wish buttons in LH sections.
     Including .lh-fp__wish here means the JS manages it directly rather than
     waiting for Swym to add swym-wishlist-collections-v2 (which it may or may
     not do, and only does asynchronously — too late for the first scan).
     Also includes .swym-add-to-wishlist which Swym uses on collection pages
     before (or instead of) promoting to swym-wishlist-collections-v2. */
  var BTN_SEL = '.swym-wishlist-collections-v2, .swym-add-to-wishlist, .lh-fp__wish, .lh-rts__wish, .lh-sr__wish, .lh-gg__wish, .lh-gg-grid__wish, .lh-bs__wish, .lh-bfr__wish, .lh-bfb__wish, .lh-zg__wish, .lh-zs__wish, .lhf__wish, .lh-gg-sc__wish';
  /* Swym's wrapping container (may or may not exist depending on theme/version) */
  var CONT_SEL = '.swym-wishlist-collections-v2-container, .swym-add-to-wishlist-container';

  /* All LH custom wish buttons (card sections + PDP).
     These get a direct _swat API click handler as a bypass for the known
     Swym init error: "querySelectorAll('.')" — caused by Swym's qa() scanner
     finding an empty class token and crashing before attaching its own handlers. */
  var LH_WISH_SEL = '.lh-fp__wish, .lh-rts__wish, .lh-sr__wish, .lh-gg__wish, .lh-gg-grid__wish, .lh-bs__wish, .lh-bfr__wish, .lh-bfb__wish, .lh-zg__wish, .lh-zs__wish, .lhf__wish, .lh-pdp__wishlist, .swym-add-to-wishlist, .swym-wishlist-collections-v2';

  /* Classes Swym adds once an item is in the wishlist */
  var WISHLISTED_CLASSES = [
    'swym-added',
    'swym-added-to-wishlist',
    'swym-is-wl'
  ];

  /* ── Helper: apply an inline !important style ── */
  function ip(el, prop, val) {
    el.style.setProperty(prop, val, 'important');
  }

  /* ── Check if a button is already in the wishlist ── */
  function isWishlisted(btn) {
    return (
      WISHLISTED_CLASSES.some(function (c) { return btn.classList.contains(c); }) ||
      btn.getAttribute('data-swym-state') === '1'
    );
  }

  /* ── Direct Swym API click handler ──────────────────────────────────────
   *
   * Swym's auto-scanner (qa function) can crash with:
   *   "querySelectorAll('.'): '.' is not a valid selector"
   * when it encounters an element whose className contains an empty token
   * (trailing space, double space, etc.).  The unhandled exception aborts
   * Swym's entire DOM-ready setup, so its own click handlers are never
   * attached to our .swym-add-to-wishlist buttons.
   *
   * This handler bypasses Swym's class scan entirely by calling
   * window._swat.addToWishList / removeFromWishList directly.
   * _swat is created by Swym before the DOM-ready scan runs, so it is
   * available even when the scan throws.
   * ─────────────────────────────────────────────────────────────────── */
  function initWishlistClick(btn) {
    if (btn.dataset.lhClickInit) return;
    btn.dataset.lhClickInit = '1';

    /* Cache aria-label variants for add/remove states (WCAG 1.4.1, 4.1.2) */
    var defaultLabel = btn.getAttribute('aria-label') || '';
    btn.dataset.ariaAdd    = defaultLabel || 'Add to wishlist';
    btn.dataset.ariaRemove = btn.dataset.ariaAdd
      .replace(/^Add\s+/i, 'Remove ')
      .replace(/\s+to wishlist$/i, ' from wishlist');
    /* Initialise aria-pressed if not already set */
    if (!btn.hasAttribute('aria-pressed')) btn.setAttribute('aria-pressed', 'false');

    btn.addEventListener('click', function (e) {
      e.stopPropagation();

      function doWishlist() {
        var swat = window._swat;
        if (!swat || typeof swat.addToWishList !== 'function') return;

        // Resolve Swym identifiers defensively: fall back to common Swym fields
        var epiAttr =
          btn.getAttribute('data-variant-id') ||
          btn.getAttribute('data-epi') ||
          (btn.dataset && (btn.dataset.epi || btn.dataset.variantId));

        var empiAttr =
          btn.getAttribute('data-product-id') ||
          btn.getAttribute('data-empi') ||
          (btn.dataset && (btn.dataset.empi || btn.dataset.productId));

        var epi  = parseInt(epiAttr, 10);
        var empi = parseInt(empiAttr, 10);
        var du   = btn.getAttribute('data-product-url') || window.location.href;

        // Bail out if required identifiers are missing/invalid
        if (!epi || isNaN(epi) || !empi || isNaN(empi)) {
          return;
        }
        if (isWishlisted(btn)) {
          swat.removeFromWishList(
            { epi: epi, empi: empi, du: du },
            function () {
              WISHLISTED_CLASSES.forEach(function (c) { btn.classList.remove(c); });
              btn.setAttribute('data-swym-state', '0');
              btn.setAttribute('aria-pressed', 'false');
              btn.setAttribute('aria-label', btn.dataset.ariaAdd || 'Add to wishlist');
            },
            function () { /* silent error */ }
          );
        } else {
          swat.addToWishList(
            { epi: epi, empi: empi, du: du },
            function () {
              btn.classList.add('swym-added');
              btn.setAttribute('data-swym-state', '1');
              btn.setAttribute('aria-pressed', 'true');
              btn.setAttribute('aria-label', btn.dataset.ariaRemove || 'Remove from wishlist');
            },
            function () { /* silent error */ }
          );
        }
      }

      /* _swat ready now → act immediately; otherwise wait for SwymLoaded */
      if (window._swat && typeof window._swat.addToWishList === 'function') {
        doWishlist();
      } else {
        window.addEventListener('SwymLoaded', function once() {
          window.removeEventListener('SwymLoaded', once);
          doWishlist();
        });
      }
    });
  }

  /* ── LH heart SVG (used to replace Swym's default icon) ── */
  var LH_HEART_SVG =
    '<svg class="lh-heart-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" ' +
    'aria-hidden="true" style="pointer-events:none;flex-shrink:0;">' +
    '<path d="M11.926 18.874c4.609-4.015 8.176-7.694 8.176-10.661 0-2.151-1.577-3.713-3.75-3.713' +
    '-1.898 0-3.477 1.393-4.426 2.989C10.977 5.893 9.398 4.5 7.5 4.5c-2.173 0-3.75 1.561-3.75 3.713' +
    ' 0 2.967 3.567 6.646 8.176 10.661z" stroke="#111111" stroke-width="1.3"/>' +
    '</svg>';

  /* ── Apply white-circle styles to a Swym button ── */
  function applyCircleStyle(btn) {
    ip(btn, 'background',       BG_DEFAULT);
    ip(btn, 'border',           'none');
    ip(btn, 'border-radius',    '50%');
    ip(btn, 'width',            BTN_SIZE);
    ip(btn, 'height',           BTN_SIZE);
    ip(btn, 'min-width',        BTN_SIZE);
    ip(btn, 'min-height',       BTN_SIZE);
    ip(btn, 'display',          'flex');
    ip(btn, 'align-items',      'center');
    ip(btn, 'justify-content',  'center');
    ip(btn, 'cursor',           'pointer');
    ip(btn, 'padding',          '0');
    ip(btn, 'box-shadow',       'none');
  }

  /* ── Hide a button (or its container) ── */
  function hide(el) {
    ip(el, 'opacity',        '0');
    ip(el, 'pointer-events', 'none');
    ip(el, 'transition',     'opacity 0.25s ease, background 0.2s ease');
  }

  /* ── Show a button (or its container) ── */
  function show(el) {
    ip(el, 'opacity',        '1');
    ip(el, 'pointer-events', 'auto');
  }

  /* ── Style an individual button element ── */
  function initButton(btn) {
    /* Mark as processed to avoid double-runs */
    if (btn.dataset.lhSwymInit) return;
    /* Skip buttons permanently hidden as Swym-injected duplicates */
    if (btn.dataset.lhHidden) return;
    btn.dataset.lhSwymInit = '1';

    applyCircleStyle(btn);

    /* For Swym-injected buttons, replace Swym's default icon with the LH heart.
       Our own __wish buttons (lh-fp__wish, lh-rts__wish) already have the
       correct SVG baked in — only replace on Swym's auto-injected buttons. */
    var isSwymInjected = btn.classList.contains('swym-wishlist-collections-v2') ||
                         btn.classList.contains('swym-add-to-wishlist');
    if (isSwymInjected && !btn.querySelector('.lh-heart-icon')) {
      /* Remove all existing child elements (Swym's icon markup) */
      while (btn.firstChild) { btn.removeChild(btn.firstChild); }
      btn.insertAdjacentHTML('beforeend', LH_HEART_SVG);
    }

    /* Always stamp position on the button itself so the placement survives
       Swym later wrapping it in a container — inline !important wins the
       cascade regardless of what Swym sets on the container. */
    ip(btn, 'position', 'absolute');
    ip(btn, 'top',      TOP_OFFSET);
    ip(btn, 'right',    RIGHT_OFFSET);
    ip(btn, 'z-index',  '3');

    /* If already inside a Swym container, position that too */
    var cont = btn.closest(CONT_SEL);
    if (cont && !cont.dataset.lhSwymInit) {
      cont.dataset.lhSwymInit = '1';
      ip(cont, 'position', 'absolute');
      ip(cont, 'top',      TOP_OFFSET);
      ip(cont, 'right',    RIGHT_OFFSET);
      ip(cont, 'z-index',  '3');
    }

    /* The element we hide/show (prefer container so Swym's own opacity on the
       button element doesn't fight us at the button level) */
    var target = cont || btn;

    /* Default: hidden (unless already wishlisted or touch device) */
    if (!isWishlisted(btn) && !IS_TOUCH) {
      hide(target);
    }

    /* Lighten circle on direct hover of the button */
    btn.addEventListener('mouseenter', function () {
      ip(btn, 'background', BG_HOVER);
    });
    btn.addEventListener('mouseleave', function () {
      ip(btn, 'background', BG_DEFAULT);
    });

    return target; /* Return for card-level handlers */
  }

  /* ── Set up hover-reveal on a card wrapper ── */
  function initCard(card) {
    /* Skip if already set up */
    if (card.dataset.lhCardInit) return;
    card.dataset.lhCardInit = '1';

    /*
     * Use the image area as the hover trigger so the heart reveal is
     * synchronised with the image swap.
     *
     * On Stiletto product cards the image swap fires on
     * .product-item__image-link:hover (scoped inside .product-item__media).
     * If we bind to the full card, hovering the title/price text below the
     * image shows the heart before the image has swapped — jarring.
     *
     * Binding to .product-item__media instead keeps both effects in the same
     * hover zone.  For our custom lh-fp / lh-rts sections we check their
     * respective image containers before falling back to the full card.
     */
    var hoverTrigger = card.querySelector('.product-item__media')
                   || card.querySelector('.lh-fp__card-media')
                   || card.querySelector('.lh-rts__image-wrap')
                   || card.querySelector('.lh-sr__media')
                   || card.querySelector('.lh-gg__card-media')
                   || card.querySelector('.lh-bs__card-media')
                   || card.querySelector('.lh-bfr__image-wrap')
                   || card.querySelector('.lh-bfb__image-wrap')
                   || card.querySelector('.lh-zg__card-media')
                   || card.querySelector('.lh-zs__card-media')
                   || card.querySelector('.lhf__media')
                   || card;

    function showAllBtns() {
      card.querySelectorAll(BTN_SEL).forEach(function (btn) {
        /* Skip buttons permanently hidden as Swym-injected duplicates */
        if (btn.dataset.lhHidden) return;
        /* Re-apply circle style every time — Swym resets inline styles during
           its own init, wiping the background we set in initButton. */
        applyCircleStyle(btn);
        /* Ensure LH icon is still present (Swym may have re-rendered) */
        var _isSwymInjected = btn.classList.contains('swym-wishlist-collections-v2') ||
                              btn.classList.contains('swym-add-to-wishlist');
        if (_isSwymInjected && !btn.querySelector('.lh-heart-icon')) {
          while (btn.firstChild) { btn.removeChild(btn.firstChild); }
          btn.insertAdjacentHTML('beforeend', LH_HEART_SVG);
        }
        var cont = btn.closest(CONT_SEL);
        show(cont || btn);
      });
    }

    function hideAllBtns(relatedTarget) {
      card.querySelectorAll(BTN_SEL).forEach(function (btn) {
        /* Skip buttons permanently hidden as Swym-injected duplicates */
        if (btn.dataset.lhHidden) return;
        /* Keep visible if already wishlisted */
        if (isWishlisted(btn)) return;
        var cont = btn.closest(CONT_SEL);
        var target = cont || btn;
        /* Don't hide if the mouse is moving directly onto the button/container —
           the Swym container sits outside .product-item__media (overflow:hidden
           clips it), so mouseleave on the media fires when the cursor slides onto
           the heart.  Suppressing the hide here keeps the heart visible. */
        if (relatedTarget && target.contains(relatedTarget)) return;
        hide(target);
      });
    }

    /* Mouse hover listeners — skip on touch devices (buttons always visible) */
    if (!IS_TOUCH) {
    hoverTrigger.addEventListener('mouseenter', showAllBtns);

    hoverTrigger.addEventListener('mouseleave', function (e) {
      hideAllBtns(e.relatedTarget);
    });
    }

    /* Also wire direct hover on each Swym container so the heart stays
       visible when the cursor is over the button itself. */
    function wireContainerHover(cont) {
      if (cont.dataset.lhContHover) return;
      cont.dataset.lhContHover = '1';
      cont.addEventListener('mouseenter', showAllBtns);
      cont.addEventListener('mouseleave', function (e) {
        /* If returning to the image area, the media mouseenter will re-show */
        hideAllBtns(e.relatedTarget);
      });
    }

    /* Wire containers already in the DOM (hover only — not needed on touch) */
    if (!IS_TOUCH) {
      card.querySelectorAll(CONT_SEL).forEach(wireContainerHover);

      /* Wire containers Swym injects later */
      var contObs = new MutationObserver(function () {
        card.querySelectorAll(CONT_SEL).forEach(wireContainerHover);
      });
      contObs.observe(card, { childList: true, subtree: true });
    }

    /* ── Keyboard focus-within accessibility (WCAG 2.4.7) ──────────────────
     * Hover show/hide uses inline !important styles that defeat CSS rules, so
     * :focus-within in a stylesheet cannot override them.  We wire focusin /
     * focusout on the card here so keyboard users (Tab key) get the same
     * reveal behaviour as mouse hover.
     * ─────────────────────────────────────────────────────────────────────── */
    card.addEventListener('focusin', function () {
      showAllBtns();
    });

    card.addEventListener('focusout', function (e) {
      /* Only hide when focus leaves the card entirely.
         e.relatedTarget is the element receiving focus next;
         if it is still inside this card, keep the buttons visible. */
      if (!card.contains(e.relatedTarget)) {
        hideAllBtns(null);
      }
    });
  }

  /* ── Main scan: find all cards + buttons and initialize them ── */
  function scan() {
    document.querySelectorAll(CARD_SEL).forEach(function (card) {
      /* Hide Swym-injected duplicates on cards that already have a custom LH
         wishlist button.  Swym's Collections V2 auto-injector can fire even
         when a hand-coded swym-button is already present inside the card,
         producing two visible hearts.  Marking with data-lh-hidden prevents
         initButton and the hover helpers from touching these elements again. */
      var LH_CUSTOM_WISH_SEL = '.lh-fp__wish, .lh-rts__wish, .lh-sr__wish, .lh-gg__wish, .lh-gg-grid__wish, .lh-bs__wish, .lh-bfr__wish, .lh-bfb__wish, .lh-zg__wish, .lh-zs__wish, .lhf__wish';
      if (card.querySelector(LH_CUSTOM_WISH_SEL)) {
        card.querySelectorAll('.swym-wishlist-collections-v2').forEach(function (duplicateBtn) {
          duplicateBtn.dataset.lhHidden = '1';
          var cont = duplicateBtn.closest(CONT_SEL);
          if (cont) { cont.dataset.lhHidden = '1'; ip(cont, 'display', 'none'); }
          else { ip(duplicateBtn, 'display', 'none'); }
        });
      }

      var btns = card.querySelectorAll(BTN_SEL);
      if (!btns.length) return;

      /* Style any buttons not yet processed */
      btns.forEach(initButton);

      /* Wire up hover once per card */
      initCard(card);
    });

    /* Wire direct API click handler on all LH wish buttons (card + PDP).
       This runs on every scan so buttons added late (theme editor, etc.)
       also get wired.  initWishlistClick's own guard prevents double-init. */
    document.querySelectorAll(LH_WISH_SEL).forEach(initWishlistClick);
  }

  /* ── MutationObserver: reacts to Swym's async DOM injection ── */
  function startObserver() {
    var timer = null;

    /* Debounce rapid mutations into a single scan */
    function schedScan() {
      if (timer) return;
      timer = setTimeout(function () {
        timer = null;
        scan();
      }, 50);
    }

    new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (m) {
        /* New nodes added anywhere (Swym injecting buttons) */
        if (m.type === 'childList' && m.addedNodes.length) return true;
        /* Class attribute changes (Swym adding swym-wishlist-collections-v2 class
           or swym-added class to existing elements) */
        if (m.type === 'attributes' && m.attributeName === 'class') return true;
        return false;
      });
      if (relevant) schedScan();
    }).observe(document.body, {
      childList:       true,
      subtree:         true,
      attributes:      true,
      attributeFilter: ['class', 'data-swym-state']
    });
  }

  /* ── Boot ── */
  function init() {
    scan();
    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Swym fires SwymLoaded once its SDK is ready — do a final scan then */
  window.addEventListener('SwymLoaded', function () {
    setTimeout(scan, 200);
  });

  /* Shopify theme editor reloads */
  document.addEventListener('shopify:section:load', function () {
    setTimeout(scan, 300);
  });

})();

/* ── Swym Wishlist: hide price for hide-price tagged products ────────────────
 * Runs independently of the hover module above.
 * Observes Swym's wishlist container for rendered items, fetches each
 * product's JSON to check for the hide-price tag, and hides the price
 * element in the wishlist UI for any matching product.
 * ─────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  /* Price + ATC elements to hide for hide-price tagged products */
  var PRICE_SELS = [
    '.swym-product-price',
    '.swym-price',
    '.swym-price-container',
    '[class*="swym"][class*="price"]',
    '.swym-add-to-cart-btn'
  ];

  /* Product link/URL selectors within a Swym wishlist item */
  var LINK_SELS = [
    'a[href*="/products/"]',
    '[data-product-url]',
    '[data-du]'
  ];

  /* Cache of processed product URLs → boolean (has hide-price tag) */
  var cache = {};

  function getProductUrl(item) {
    for (var i = 0; i < LINK_SELS.length; i++) {
      var el = item.querySelector(LINK_SELS[i]);
      if (el) {
        return el.getAttribute('data-product-url') ||
               el.getAttribute('data-du') ||
               el.getAttribute('href');
      }
    }
    return null;
  }

  function hidePriceIn(item) {
    PRICE_SELS.forEach(function (sel) {
      item.querySelectorAll(sel).forEach(function (el) {
        el.style.setProperty('display', 'none', 'important');
      });
    });
  }

  function checkAndHide(item) {
    if (item.dataset.lhPriceChecked) return;
    item.dataset.lhPriceChecked = '1';

    var rawUrl = getProductUrl(item);
    if (!rawUrl) return;

    /* Normalise to a path like /products/handle */
    var path = rawUrl.replace(/^https?:\/\/[^/]+/, '').split('?')[0];
    var jsonUrl = path.replace(/\/$/, '') + '.js';

    if (cache[jsonUrl] === true) { hidePriceIn(item); return; }
    if (cache[jsonUrl] === false) return;

    fetch(jsonUrl)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        var hide = Array.isArray(data.tags) && data.tags.indexOf('hide-price') !== -1;
        cache[jsonUrl] = hide;
        if (hide) hidePriceIn(item);
      })
      .catch(function () { cache[jsonUrl] = false; });
  }

  function scanWishlist() {
    /* Swym wishlist item selectors across widget versions */
    var items = document.querySelectorAll(
      '.swym-wishlist-item, .swym-product, [class*="swym"][class*="item"], [class*="swym"][class*="product"]'
    );
    items.forEach(checkAndHide);
  }

  /* Observe the entire body for Swym's async wishlist render */
  var wlObserver = new MutationObserver(function (mutations) {
    var relevant = mutations.some(function (m) {
      return m.type === 'childList' && m.addedNodes.length;
    });
    if (relevant) scanWishlist();
  });

  function startWishlistObserver() {
    wlObserver.observe(document.body, { childList: true, subtree: true });
    scanWishlist();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWishlistObserver);
  } else {
    startWishlistObserver();
  }

  window.addEventListener('SwymLoaded', function () {
    setTimeout(scanWishlist, 400);
  });

})();
